import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useVoiceInput, useSpeaker } from "@/lib/voice";
import { serviceMeta } from "@/lib/ui";
import { Mic, Square, Check, RotateCcw, Calendar, MapPin, Loader2 } from "lucide-react";

export default function VoiceRequest({ onPosted }) {
  const [draft, setDraft] = useState(null);
  const [heard, setHeard] = useState("");
  const [parsing, setParsing] = useState(false);
  const [posting, setPosting] = useState(false);
  const { speak, stop } = useSpeaker();
  const nav = useNavigate();

  const onText = async (text) => {
    setHeard(text);
    setParsing(true);
    try {
      const { data } = await api.post("/ai/parse-request", { text });
      setDraft(data);
      speak(`${data.spoken_summary} Tap the green button to post it, or the grey one to say it again.`);
    } catch (e) {
      const msg = e.response?.data?.detail || "I couldn't understand that. Please try again.";
      toast.error(msg); speak(msg);
    } finally { setParsing(false); }
  };
  const voice = useVoiceInput(onText);

  const begin = () => { stop(); setDraft(null); setHeard(""); voice.start(); };
  const post = async () => {
    setPosting(true);
    try {
      const { spoken_summary, ...body } = draft;
      const { data } = await api.post("/requests", body);
      speak("Done. Your request is posted. Helpers nearby can now see it.");
      toast.success("Your request is posted");
      setDraft(null); setHeard("");
      onPosted?.();
      nav(`/requests/${data.id}`);
    } catch { toast.error("Couldn't post it. Please try again."); }
    finally { setPosting(false); }
  };

  const busy = voice.transcribing || parsing;
  const meta = draft ? serviceMeta(draft.service_type) : null;

  return (
    <section data-testid="voice-request" className="bg-[#2E7D6B] text-white rounded-3xl p-6 sm:p-8 mb-8">
      {!draft && (
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <button data-testid="voice-request-mic" onClick={voice.recording ? voice.stop : begin} disabled={busy}
            aria-label={voice.recording ? "Stop and send" : "Tell us what you need"}
            className={`w-28 h-28 rounded-full flex items-center justify-center shrink-0 shadow-xl transition-transform active:scale-95 ${voice.recording ? "bg-red-500 animate-pulse" : "bg-[#D96B43] hover:bg-[#C25832]"} disabled:opacity-60`}>
            {busy ? <Loader2 className="w-12 h-12 animate-spin" /> : voice.recording ? <Square className="w-12 h-12" fill="white" /> : <Mic className="w-14 h-14" />}
          </button>
          <div className="text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-display font-bold">Just tell us what you need</h2>
            <p className="text-lg text-white/85 mt-2" data-testid="voice-request-status">
              {voice.recording ? "Listening… tap the red button when you're done." : voice.transcribing ? "Writing down what you said…" : parsing ? "Getting your request ready…"
                : "Tap the microphone and say, for example: “I need someone to take me to the eye clinic on Tuesday morning.”"}
            </p>
            {heard && !draft && <p className="mt-2 text-white/70 italic">“{heard}”</p>}
          </div>
        </div>
      )}
      {draft && (
        <div data-testid="voice-request-draft">
          <p className="text-white/80 text-sm font-semibold tracking-wide">I HEARD: “{heard}”</p>
          <div className="bg-white text-slate-900 rounded-2xl p-6 mt-4">
            <div className="flex items-start gap-4">
              <span className="w-14 h-14 rounded-xl bg-[#FBEBE4] flex items-center justify-center shrink-0"><meta.icon className="w-7 h-7 text-[#D96B43]" /></span>
              <div>
                <h3 data-testid="voice-draft-title" className="text-2xl font-semibold">{draft.title}</h3>
                <p className="text-lg text-slate-700 mt-1">{draft.description}</p>
                <p className="flex items-center gap-2 text-slate-600 mt-3 text-lg"><Calendar className="w-5 h-5 text-[#2E7D6B]" /> {draft.scheduled_at.replace("T", " at ")}</p>
                <p className="flex items-center gap-2 text-slate-600 text-lg"><MapPin className="w-5 h-5 text-[#2E7D6B]" /> {draft.location || "Your home"}</p>
                <p className="text-slate-500 mt-1">{meta.label} · {draft.payment_type === "paid" ? `Paid, ₹${draft.amount}` : "Voluntary"}{draft.urgency === "urgent" ? " · Urgent" : ""}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-5">
            <button data-testid="voice-draft-post" onClick={post} disabled={posting}
              className="flex-1 min-w-[200px] bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-60 text-white text-xl font-semibold min-h-[64px] rounded-2xl flex items-center justify-center gap-3">
              <Check className="w-7 h-7" /> {posting ? "Posting…" : "Yes, post it"}
            </button>
            <button data-testid="voice-draft-retry" onClick={begin}
              className="flex-1 min-w-[200px] bg-white/15 hover:bg-white/25 text-white text-xl font-semibold min-h-[64px] rounded-2xl flex items-center justify-center gap-3">
              <RotateCcw className="w-6 h-6" /> Say it again
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
